<script setup lang="ts">
import { useElementBounding } from '@vueuse/core'
import SeperatorImg from '@/assets/components/categoryOffersBlock/separator.webp?url'
import bgGamePass from '@/assets/components/gameCategory/bg_pattern_blue.webp?url'
import bgOthers from '@/assets/components/gameCategory/bg_pattern_green.webp?url'
import bgSpecial from '@/assets/components/gameCategory/bg_pattern_yellow.webp?url'
import CategoryOffersBlockComponent from '@/components/games/CategoryOffersBlockComponent.vue'
import { openConnectionDialog } from '@/components/shared/GoatConnectGameDialog/GoatConnectGameDialog'

const props = defineProps<Props>()
const { t } = useI18n()
interface Props {
  offers?: { [key in Games.GameKeyType]?: Array<Games.Offer> }
  freeOffers?: boolean
}
type CategoryKey = 'all' | 'special' | 'gamePasses' | 'gems' | 'others'
const { routeGame, isLogged, isLoggedGoat, showLogInDialog } = gamesUtils
const { gaSections } = gaUtils
const catOffers = computed(() => {
  const catOffers: Record<CategoryKey, Array<Games.Offer>> = {
    all: [],
    gamePasses: [],
    special: [],
    gems: [],
    others: []
  }
  if (routeGame.value?.gameKey) {
    (props?.offers?.[routeGame.value.gameKey] || []).forEach((offer) => {
      catOffers.all.push(offer)
      const offerType = offer?.type
      if (offerType === 'special' || offerType === 'gamePasses' || offerType === 'gems') {
        catOffers[offerType].push(offer)
      } else if (offerType === 'firstbuygems') {
        catOffers.gems.push(offer)
      } else {
        catOffers.others.push(offer)
      }
    })
  }
  return catOffers
})

function scrollToTarget(elementID: string) {
  if (!isLogged.value) {
    if (isLoggedGoat.value) {
      openConnectionDialog(routeGame.value?.gameKey, false)
    } else {
      showLogInDialog(true, { gs_game: routeGame.value?.gameKey, gs_route: String(routeGame.value?.name), gs_section: gaSections.freeButton })
    }
    return false
  }
  const el = document.getElementById(elementID)
  el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const freeBoxBottomEl = useTemplateRef('freeBoxBottom')
const { top: topFreeBoxBottomEl } = useElementBounding(freeBoxBottomEl)
const changeFreeIcon = computed(() => {
  if (!(routeGame.value?.freeIcon && props.freeOffers)) {
    return false
  }
  return topFreeBoxBottomEl.value <= 150
})
</script>

<template>
  <div class="w-full m-auto  mx-auto">
    <template v-if="!isLogged || (routeGame?.freeIcon && freeOffers)">
      <transition
        name="el-fade-in-linear"
        mode="out-in"
      >
        <img
          v-if="changeFreeIcon"
          alt="free icon"
          class="sticky top-16 big:top-24 left-auto h-28 -mb-28 translate-y-1 sm:translate-y-2.5 xl:right-1/2 xl:translate-x-[min(37rem,48vw)] ml-auto mr-0 z-20 cursor-pointer transition-all hover:scale-110"
          :src="routeGame?.freeIconUp"
          @click="scrollToTarget('freeBox')"
        >
        <img
          v-else
          alt="free icon"
          class="sticky top-16 big:top-24 left-auto h-28 -mb-28 translate-y-1 sm:translate-y-2.5 xl:right-1/2 xl:translate-x-[min(37rem,48vw)] ml-auto mr-0 z-20 cursor-pointer transition-all hover:scale-110"
          :src="routeGame?.freeIcon"
          @click="scrollToTarget('freeBox')"
        >
      </transition>
    </template>
    <img
      alt=""
      class="w-full absolute lef-0 right-0 -translate-y-1/2 z-10"
      :src="SeperatorImg"
    >
    <CategoryOffersBlockComponent
      :cat-offers="catOffers.special"
      :title="t('gameCategory.storeSpecials')"
      block-custom-class="bg-[#ffba15] text-black"
      tile-size-class="w-full big:w-1/2"
      :bg-img="bgSpecial"
    />
    <img
      alt=""
      class="w-full absolute lef-0 right-0 -translate-y-1/2 z-10"
      :src="SeperatorImg"
    >
    <CategoryOffersBlockComponent
      :cat-offers="catOffers.gamePasses"
      :title="t('gameCategory.gamePass')"
      block-custom-class="bg-[#032b5e] text-white"
      tile-size-class="w-full big:w-1/2"
      :bg-img="bgGamePass"
    />
    <img
      alt=""
      class="w-full absolute lef-0 right-0 -translate-y-1/2 z-10"
      :src="SeperatorImg"
    >
    <CategoryOffersBlockComponent
      :cat-offers="catOffers.others"
      :title="t('gameCategory.gameOffers')"
      block-custom-class="bg-[#13434b] text-white"
      tile-size-class="w-full big:w-1/2"
      :bg-img="bgOthers"
    />
    <img
      alt=""
      class="w-full absolute lef-0 right-0 -translate-y-1/2 z-10"
      :src="SeperatorImg"
    >
    <slot name="free" />
    <div
      id="freeBoxBottom"
      ref="freeBoxBottom"
      class="h-0"
    />
    <img
      v-if="!!$slots.free"
      alt=""
      class="w-full absolute lef-0 right-0 -translate-y-1/2 z-10"
      :src="SeperatorImg"
    >
    <CategoryOffersBlockComponent
      :cat-offers="catOffers.gems"
      :title="t(routeGame?.gemTabName || 'gameCategory.gems')"
      block-custom-class="bg-[#13434b] text-white"
      tile-size-class="w-1/2 min-w-28 big:w-1/3"
      :bg-img="bgOthers"
      gems-type
      :discount-ribbon-size="{ miniGems: true }"
      gap-class="gap-y-4"
    />
    <img
      alt=""
      class="w-full absolute lef-0 right-0 -translate-y-1/2  z-10 hidden big:block"
      :src="SeperatorImg"
    >
    <div class="mt-12 hidden big:block" />
  </div>
</template>

<style scoped lang="postcss"></style>
